import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import ArrowLeft from '@decisiv/iconix/lib/components/ArrowLeft';

import Button from '@decisiv/ui-components/lib/components/Button';
import Wizard from '@decisiv/ui-components/lib/components/Wizard';

import AccountStep from './AccountStep';
import AcknowledgmentPanel from './AcknowledgmentPanel';
import CreatingPanel from './CreatingPanel';
import ErrorPanel from './ErrorPanel';
import PermissionsStep from './PermissionsStep';
import SuccessPanel from './SuccessPanel';
import UserDetailsStep from './UserDetailsStep';
import {
  CREATING_PANEL,
  EXISTING_USER,
  STEPS,
  SUCCESS_PANEL,
  WIZARD_TITLE,
} from './copy';
import { WIZARD_Z_INDEX } from './layers';
import { toFieldMessages } from './serverErrors';
import { FooterActions } from './styles';
import useAuthenticationOptions from '../../hooks/useAuthenticationOptions';
import useCreateUserForm, { toPayload } from '../../hooks/useCreateUserForm';
import useFormOptions from '../../hooks/useFormOptions';
import {
  createUser,
  resolveExistingUser,
} from '../../services/createUserService';

export const PHASES = {
  form: 'form',
  creating: 'creating',
  success: 'success',
  error: 'error',
};

const RESULT_KIND_FOR_SCENARIO = {
  restore: 'restored',
  add: 'added',
  acknowledge: 'updated',
};

const toItems = (options) =>
  options.map(({ label, value }) => ({ id: String(value), label }));

/**
 * The Create User wizard (PD-4064).
 *
 * The KeyDS Wizard component is what the mockup draws — same centred title, same
 * StepTracker, same Cancel / Back / Continue footer — so the shell is the
 * library's and only the step bodies are ours.
 *
 * Four phases share the last step's slot. `form` is the normal flow; submitting
 * moves to `creating`, then to `success` or `error`. The tracker keeps showing
 * the last step throughout, as in the mockup, and `renderActions` replaces the
 * footer, because none of the three terminal phases wants Back and Continue.
 */
function CreateUserWizard({ onClose, onCreated, visible }) {
  const [activeStep, setActiveStep] = useState(1);
  const [phase, setPhase] = useState(PHASES.form);
  const [createdUser, setCreatedUser] = useState(null);
  const [resultKind, setResultKind] = useState('created');
  const [skippedCount, setSkippedCount] = useState(0);
  const [resolveErrorMessage, setResolveErrorMessage] = useState(null);

  // Whether anything was created since the wizard was last closed, which is a
  // different question from who the success panel names: CREATE NEW USER clears
  // `createdUser` to start a fresh form, and cancelling from there still leaves a
  // user the list has not fetched. Kept separate so that refetch cannot be lost.
  const [hasCreated, setHasCreated] = useState(false);
  const operationToken = useRef(0);

  const {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    options,
    searchAccounts,
  } = useFormOptions(visible);
  const {
    checks,
    checkEmail,
    checkMobile,
    draft,
    reset,
    serverMessages,
    setField,
    setServerMessages,
    stepValidity,
  } = useCreateUserForm();

  // Declared before the hook below so it can put a locked provider into the draft.
  // Stable, because the hook reconciles the draft in an effect keyed on it.
  const setSsoProvider = useCallback(
    (value) => setField('ssoProvider', value),
    [setField],
  );

  const {
    authentication,
    isAskable,
    isLoadingAuthentication,
  } = useAuthenticationOptions({
    accountIds: draft.accountIds,
    email: draft.email,
    onChange: setSsoProvider,
    value: draft.ssoProvider,
  });

  const validity = stepValidity();
  const isAcknowledgeScenario = checks.existingUser?.scenario === 'acknowledge';

  const restart = () => {
    operationToken.current += 1;
    reset();
    setCreatedUser(null);
    setResultKind('created');
    setSkippedCount(0);
    setResolveErrorMessage(null);
    setPhase(PHASES.form);
    setActiveStep(1);
  };

  // However the wizard is left — CLOSE from the success panel, or CANCEL from a
  // form the admin opened with CREATE NEW USER and then abandoned — a user created
  // in it is only in the list once the page refetches, so tell it on the way out.
  // One call however many were created: the page bumps a counter, so a single
  // refetch brings all of them in.
  const close = () => {
    if (hasCreated) onCreated();

    setHasCreated(false);
    restart();
    onClose();
  };

  const submit = async () => {
    const token = operationToken.current;
    setResultKind('created');
    setSkippedCount(0);
    setResolveErrorMessage(null);
    setPhase(PHASES.creating);
    setServerMessages({});

    try {
      const created = await createUser(toPayload(draft));
      if (operationToken.current !== token) return;

      setCreatedUser(created);
      setHasCreated(true);
      setPhase(PHASES.success);
    } catch (error) {
      if (operationToken.current !== token) return;

      // The server knows things the wizard cannot check — the per-country phone
      // rules, an email taken since the blur check, and every other rule on User.
      // When it says which field was wrong, show that on the field's own step
      // instead of the generic panel, which is what made a rejected phone number
      // look like a server fault.
      const { messages, step } = toFieldMessages(error.fieldErrors);

      if (step === null) {
        setPhase(PHASES.error);
        return;
      }

      setServerMessages(messages);
      setActiveStep(step);
      setPhase(PHASES.form);
    }
  };

  const resolveExisting = async () => {
    const token = operationToken.current;
    const {
      addAccountIds,
      id,
      restoreAccountIds,
      scenario,
    } = checks.existingUser;

    setResultKind(RESULT_KIND_FOR_SCENARIO[scenario]);
    setSkippedCount(0);
    setResolveErrorMessage(null);
    setActiveStep(3);
    setPhase(PHASES.creating);

    try {
      const {
        user: created,
        skippedRestoreAccountIds,
        skippedAddAccountIds,
      } = await resolveExistingUser({
        userId: id,
        restoreAccountIds,
        addAccountIds,
      });
      if (operationToken.current !== token) return;

      setCreatedUser(created);
      setSkippedCount(
        skippedRestoreAccountIds.length + skippedAddAccountIds.length,
      );
      setHasCreated(true);
      setPhase(PHASES.success);
    } catch (error) {
      if (operationToken.current !== token) return;

      // Unlike submit's per-field errors, this endpoint's failures arrive as
      // copy meant to be shown as-is — self-modification, nothing left to
      // resolve, and so on — under `base`, which toFieldMessages has no field
      // to map it to. Without this, RETRY would just repeat the same named
      // failure behind a generic "unexpected error" forever.
      setResolveErrorMessage(error.fieldErrors?.base?.[0] ?? null);
      setPhase(PHASES.error);
    }
  };

  const terminalContent = () => {
    if (phase === PHASES.creating) return <CreatingPanel kind={resultKind} />;
    if (phase === PHASES.error) {
      return (
        <ErrorPanel
          body={resultKind === 'created' ? null : resolveErrorMessage}
          onRetry={resultKind === 'created' ? submit : resolveExisting}
        />
      );
    }

    return (
      <SuccessPanel
        kind={resultKind}
        skippedCount={skippedCount}
        user={createdUser}
        onCreateAnother={restart}
      />
    );
  };

  // Step 3's slot: the ordinary Permissions form, unless a terminal phase has
  // taken it over, or the matched user's accounts split into both a restore
  // and an add (see AcknowledgmentPanel).
  const lastStepContent = () => {
    if (phase !== PHASES.form) return terminalContent();
    if (isAcknowledgeScenario) {
      return <AcknowledgmentPanel existingUser={checks.existingUser} />;
    }

    return (
      <PermissionsStep
        accountCount={draft.accountIds.length}
        draft={draft}
        permissionOptions={options.permissions}
        onChange={setField}
      />
    );
  };

  const steps = [
    {
      title: STEPS.account,
      hideBodyTitle: true,
      nextButtonProps: {
        disabled: !validity[0],
        onClick: () => {
          if (draft.email.trim()) checkEmail();
          setActiveStep(2);
        },
      },
      content: (
        <AccountStep
          hasMore={hasMoreAccounts}
          isLoading={isLoadingAccounts}
          options={accountOptions}
          selected={draft.accountIds}
          total={accountsTotal}
          onChange={(values) => setField('accountIds', values)}
          onLoadMore={loadMoreAccounts}
          onSearchAccounts={searchAccounts}
        />
      ),
    },
    {
      title: STEPS.details,
      hideBodyTitle: true,
      previousButtonProps: { onClick: () => setActiveStep(1) },
      nextButtonProps: {
        disabled: !validity[1],
        onClick: () => setActiveStep(3),
      },
      content: (
        <UserDetailsStep
          authentication={authentication}
          authenticationOptions={toItems(authentication.options)}
          checks={checks}
          draft={draft}
          isAskable={isAskable}
          isLoadingAuthentication={isLoadingAuthentication}
          jobRoleOptions={toItems(options.jobRoles)}
          phoneCountryCodeOptions={toItems(options.phoneCountryCodes)}
          serverMessages={serverMessages}
          onBlurEmail={checkEmail}
          onBlurMobile={checkMobile}
          onChange={setField}
          onResolveExisting={resolveExisting}
        />
      ),
    },
    {
      title: isAcknowledgeScenario ? STEPS.acknowledge : STEPS.permissions,
      hideBodyTitle: true,
      previousButtonProps: { onClick: () => setActiveStep(2) },
      content: lastStepContent(),
    },
  ];

  // The last step's own footer. Ordinarily CREATE USER, which the Wizard
  // renders because it is the submit button of the final step — but the
  // acknowledgment screen has no permissions to require and submits through
  // `resolveExisting` instead, same as the ExistingUserCard's own button does
  // for the other two scenarios.
  const submitButtonProps = isAcknowledgeScenario
    ? { text: EXISTING_USER.acknowledge.confirm, onClick: resolveExisting }
    : { disabled: !validity[2], text: 'Create User', onClick: submit };

  return (
    <Wizard
      activeStep={activeStep}
      id="create-user-wizard"
      steps={steps}
      submitButtonProps={submitButtonProps}
      title={WIZARD_TITLE}
      visible={visible}
      zIndex={WIZARD_Z_INDEX}
      onCancel={close}
      renderActions={({ defaultActions, isLastStep }) => {
        if (!isLastStep || phase === PHASES.form) return defaultActions;

        // Creating: no way out but waiting, per the mockup's disabled action.
        if (phase === PHASES.creating) {
          return (
            <FooterActions>
              <Button disabled text={CREATING_PANEL[resultKind].action} />
            </FooterActions>
          );
        }

        // Success closes; error offers a way back to the form as well. Both go
        // through FooterActions — see ./styles for why bare Buttons here end up
        // at opposite ends of the footer.
        return (
          <FooterActions>
            {phase === PHASES.error && (
              <Button
                kind="secondary"
                icon={ArrowLeft}
                text="Back"
                onClick={() => setPhase(PHASES.form)}
              />
            )}
            <Button text={SUCCESS_PANEL.close} onClick={close} />
          </FooterActions>
        );
      }}
    />
  );
}

CreateUserWizard.propTypes = {
  onClose: PropTypes.func.isRequired,
  /** Called when a user was created, so the list can refetch. */
  onCreated: PropTypes.func.isRequired,
  visible: PropTypes.bool,
};

CreateUserWizard.defaultProps = {
  visible: false,
};

export default CreateUserWizard;
